import * as React from "react";
import { Text } from "components/form";
import InsertVariableButton from "components/form/InsertVariableButton/InsertVariableButton";
import styles = require("./style.less");
import { ScriptingLanguage } from "components/scriptingLanguage";
import isBound from "components/form/BoundField/isBound";
import {PropertyValueResource} from "client/resources/propertyValueResource";
import * as cn from "classnames";
import IconButton from "components/IconButton/IconButton";
import IconButtonList from "components/IconButtonList/IconButtonList";
import {debounce} from "lodash";
import {TextInput} from "components/form/Text/Text";
const bindIcon = require("./bind.svg");
const unbindIcon = require("./unbind.svg");

export type ResetValue = PropertyValueResource | (() => PropertyValueResource);

interface BoundFieldProps {
    localNames?: string[];
    resetValue: ResetValue;
    projectId?: string;
    parameter?: { DisplaySettings: { "Octopus.ControlType": "Checkbox" | "Select" | "Sensitive" } };
    value?: string;
    syntax?: ScriptingLanguage;
    hideBindButton?: boolean;
    showVariables?: boolean; //if showVariables = true we don't need projectId or localNames, but leave required since we _almost_ always needs them
    label?: string | JSX.Element;
    wide?: boolean;
    isBound?: boolean; // if you want to track isBound externally
    onIsBoundChanged?(value: boolean): void;
    onChange?(newValue: string): void;
}

interface BoundFieldState {
    isBound: boolean;
    needsSpecialTextBox: boolean;
    isInitialised: boolean;
}

// This is a placeholder until we can implement BoundField to match the new designs
export default class BoundField extends React.Component<BoundFieldProps, BoundFieldState> {
    public static defaultProps: Partial<BoundFieldProps> = {
        hideBindButton: false,
        showVariables: true,
        wide: false
    };

    private inputRef: any;
    private textInputRef: TextInput | null = null;

    private parameterUnboundValueRanges = {
        Checkbox: (param: any, value: any) => {
            if (!value) {
                return false;
            }
            const lower = value.toLowerCase();
            return lower === "true" || lower === "false";
        },
        Select: (param: any, value: any) => {
            const selectOptions = param["Octopus.SelectOptions"];
            const options = selectOptions ? param["Octopus.SelectOptions"].split("\n") : [];
            return options.map((option: string) => option.split("|")[0]).indexOf(value) >= 0;
        },
        Sensitive: (param: any, value: any) => {
            return typeof value === "undefined" || typeof value === "object";
        }
    };

    constructor(props: BoundFieldProps) {
        super(props);
        this.state = {
            isBound: false,
            needsSpecialTextBox: false,
            isInitialised: false
        };
        this.bindUnbind = debounce(this.bindUnbind, 500);
    }

    componentDidMount() {
        this.validateChild();
        this.initialiseNeedsSpecialTextBox();
        this.initialiseIsBound();
        this.setState({ isInitialised: true });
    }

    render() {
        if (!this.state.isInitialised) {
            return null;
        }

        const child = this.onlyChild();
        const component = this.state.needsSpecialTextBox && this.getIsBound()
            ? <Text
                label={this.props.label || child.props.label}
                value={this.props.value || child.props.value}
                onChange={this.props.onChange || child.props.onChange}
                multiLine={true}
                rows={5}
                rowsMax={5}
                textInputRef={ref => this.textInputRef = ref}
            />
            : React.cloneElement(child, {
                ref: (node: any) => {
                  // https://github.com/facebook/react/issues/8873#issuecomment-275423780
                  // Add our ref
                  this.inputRef = node;
                  // Call the original ref, if any
                  const {ref} = child as any;
                  if (typeof ref === "function") {
                    ref(node);
                  }
                }
            });

        const insertVariable = (!this.state.needsSpecialTextBox || this.getIsBound()) && this.props.showVariables
            ? <InsertVariableButton
                syntax={this.props.syntax}
                projectId={this.props.projectId}
                localNames={this.props.localNames}
                onSelected={v => this.insert(v)} />
            : null;

        const containerClassName = cn({[styles.container]: true, [styles.wide]: this.props.wide});
        const buttonsClassName = cn({[styles.buttons]: !this.props.wide});
        return <div className={containerClassName}>
            {component}
            <div className={buttonsClassName}>
                <IconButtonList buttons={[insertVariable, this.binding()]} />
            </div>
        </div>;
    }

    private getIsBound(): boolean {
        return this.props.isBound || this.state.isBound;
    }

    private binding() {
        if (!this.state.needsSpecialTextBox || this.props.hideBindButton) {
            return null;
        }

        const text = this.getIsBound()
            /*tabIndex on svg is not supported in IE11, need to use focusable instead*/
            ? "Unbind"
            : "Bind";

        const icon = this.getIsBound()
            ? unbindIcon
            : bindIcon;

        return <IconButton onClick={this.bindUnbind} toolTipContent={text} icon={icon} />;
    }

    // TODO: This needs the <Clear/> unbind icon set up properly so it switches from code icon to clear icon.
    // The clear icons is used on fields to remove or reset so this felt consistent.

    private insert(val: string) {
        const ref = this.textInputRef ? this.textInputRef : this.inputRef;
        if (ref && ref.insertAtCursor && ref.focus) {
            ref.insertAtCursor(val);
            ref.focus();
        }
    }

    private validateChild() {
        const child = this.onlyChild();
        if (!this.props.hasOwnProperty("onChange") && !child.props.hasOwnProperty("onChange")) {
            throw new Error("If BoundField is not supplied an onChange function prop the child control must have one");
        }
        if (!this.props.hasOwnProperty("value") && !child.props.hasOwnProperty("value")) {
            throw new Error("If BoundField is not supplied a value prop the child control must have one");
        }
    }

    private initialiseNeedsSpecialTextBox() {
        const child = this.onlyChild();
        const needsSpecialTextBox = child.props.hasOwnProperty("needsBoundFieldTextbox")
            ? child.props.needsBoundFieldTextbox
            : true;
        this.setState({
            needsSpecialTextBox
        });
    }

    private initialiseIsBound() {
        const value = this.getValue();
        if (value && typeof (value) === "string") {
            if (isBound(value)) {
                this.bind();
            }
            if (this.props.parameter) {
                const displaySettings = this.props.parameter.DisplaySettings;
                const controlType = displaySettings["Octopus.ControlType"];
                const isInRange = this.parameterUnboundValueRanges[controlType];
                if (isInRange) {
                    if (!isInRange(displaySettings, value)) {
                        this.bind();
                    }
                }
            }
        }
    }

    // enforce single child, which React.Children.only should do
    // but it fails for single child in an array.
    private onlyChild() {
        if (Array.isArray(this.props.children) && this.props.children.length === 1) {
            return React.Children.only(this.props.children[0]);
        }
        return React.Children.only(this.props.children);
    }

    // if value has been provided, use that, otherwise use the child's
    private getValue() {
        const value = this.props.value  ? this.props.value : this.onlyChild().props.value;
        return value || "";
    }

    // if onChange has been provided, use that, otherwise use the child's
    private getOnChange() {
        if (this.props.hasOwnProperty("onChange")) {
            return this.props.onChange;
        }
        return this.onlyChild().props.onChange;
    }

    private boundText() {
        if (this.getIsBound()) {
            return "Remove custom bindings from this field";
        }
        return "Apply custom bindings to this field";
    }

    private bindUnbind = () => {
        // this is debounced as we are getting two clicks when hitting enter to
        // activate the button and can't get to the bottom of it
        if (this.getIsBound()) {
            this.unbind();
        } else {
            this.bind();
        }
    }

    private bind = () => {
        const value = this.getValue();
        const onChange = this.getOnChange();
        if (typeof (value) !== "string") {
            onChange("");
        }
        this.setState({ isBound: true });
        if (this.props.onIsBoundChanged) {
            this.props.onIsBoundChanged(true);
        }
    }

    private unbind = () => {
        const onChange = this.getOnChange();
        this.setState({ isBound: false });
        if (this.props.onIsBoundChanged) {
            this.props.onIsBoundChanged(false);
        }

        if (typeof (this.props.resetValue) === "function") {
            onChange(this.props.resetValue());
        } else {
            onChange(this.props.resetValue);
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/form/BoundField/BoundField.tsx